import { useEffect, useRef, useCallback } from 'react';
import { useEntraAuth } from '../components/EntraAuth';

const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

export const useInactivityLogout = () => {
    const { logout, isAuthenticated } = useEntraAuth();
    const timeoutRef = useRef(null);
    const lastActivityRef = useRef(Date.now());

    const resetTimer = useCallback(() => {
        lastActivityRef.current = Date.now();

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        if (isAuthenticated) {
            timeoutRef.current = setTimeout(async () => {
                console.log('🔄 Auto-logout due to inactivity');
                try {
                    await logout();
                    alert('You have been automatically logged out due to inactivity.');
                } catch (error) {
                    console.error('Auto-logout failed:', error);
                }
            }, INACTIVITY_TIMEOUT);
        }
    }, [isAuthenticated, logout]);

    const handleActivity = useCallback(() => {
        resetTimer();
    }, [resetTimer]);

    useEffect(() => {
        if (!isAuthenticated) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            return;
        }

        // Set up event listeners for user activity
        const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

        events.forEach(event => {
            document.addEventListener(event, handleActivity, true);
        });

        // Start the timer initially
        resetTimer();

        // Cleanup function
        return () => {
            events.forEach(event => {
                document.removeEventListener(event, handleActivity, true);
            });

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [isAuthenticated, handleActivity, resetTimer]);

    // Return remaining time for optional UI display
    const getRemainingTime = useCallback(() => {
        if (!isAuthenticated || !timeoutRef.current) return 0;
        const elapsed = Date.now() - lastActivityRef.current;
        return Math.max(0, INACTIVITY_TIMEOUT - elapsed);
    }, [isAuthenticated]);

    return { getRemainingTime };
};
